import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Contact = () => {
  const [contactFormData, setContactFormData] = useState({
    fullName: '',
    email: '',
    subject: '',
    message: ''
  });

  const [contactFormStatus, setContactFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactFormStatus('submitting');

    try {
      const formData = new URLSearchParams();
      Object.entries(contactFormData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d2ramls2132hm7p4bq70', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      });

      if (response.ok) {
        setContactFormStatus('success');
        setContactFormData({ fullName: '', email: '', subject: '', message: '' });
      } else {
        setContactFormStatus('error');
      }
    } catch (error) {
      setContactFormStatus('error');
    }
  };

  return (
    <div className="min-h-screen" dir="ltr">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-[1300] overflow-visible h-[72px]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-[72px]">
            <Link className="flex items-center" to="/">
              <img 
                alt="VIPA Trading Company" 
                className="h-16 w-auto" 
                src="https://static.readdy.ai/image/b722d8dcf1c144135667d61789b02d4e/99b5c3dffef714fa12a155204a44cba1.png"
              />
            </Link>
            
            <nav className="hidden lg:flex items-center space-x-8">
              <Link className="text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium" to="/">
                Home
              </Link>
              <div className="relative">
                <button 
                  className="text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium flex items-center cursor-pointer whitespace-nowrap focus:outline-none focus:text-[#E71B25]"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  Services
                  <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {isDropdownOpen && (
                  <div className="absolute top-full left-0 mt-0 min-w-[260px] bg-white rounded-lg shadow-lg border border-[#C3AAAB33] z-[1000]">
                    <div className="py-2">
                      <Link to="/services/construction" className="block px-4 py-3 text-[#595A5C] hover:bg-[#F7F7F7] hover:text-[#E71B25] transition-colors text-left">
                        Construction & Roads
                      </Link>
                      <Link to="/services/oil" className="block px-4 py-3 text-[#595A5C] hover:bg-[#F7F7F7] hover:text-[#E71B25] transition-colors text-left">
                        Oil & Gas
                      </Link>
                      <Link to="/services/gold" className="block px-4 py-3 text-[#595A5C] hover:bg-[#F7F7F7] hover:text-[#E71B25] transition-colors text-left">
                        Gold Trading
                      </Link>
                      <Link to="/services/defense" className="block px-4 py-3 text-[#595A5C] hover:bg-[#F7F7F7] hover:text-[#E71B25] transition-colors text-left">
                        Defense Systems
                      </Link>
                      <Link to="/consultation" className="block px-4 py-3 text-[#595A5C] hover:bg-[#F7F7F7] hover:text-[#E71B25] transition-colors text-left">
                        Consulting
                      </Link>
                    </div>
                  </div>
                )}
              </div>
              <Link className="text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium" to="/about">
                About Us
              </Link>
              <Link className="text-[#E71B25] hover:text-[#d11620] transition-colors font-medium" to="/contact">
                Contact
              </Link>
            </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <a data-lang="ar" className="text-[#595A5C] hover:text-[#E71B25] transition-colors font-medium cursor-pointer">
                العربية
              </a>
              <Link to="/contact">
                <button 
                  type="button" 
                  className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap border-2 border-[#E71B25] text-[#E71B25] hover:bg-[#E71B25] hover:text-white px-4 py-2 text-sm"
                >
                  Email Contact
                </button>
              </Link>
            </div>

            <button 
              className="lg:hidden text-[#595A5C] cursor-pointer" 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We are here to answer your inquiries and help you at any time. Contact us and we will be happy to assist you.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="bg-white">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#E71B25] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">info@qparadingco.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#E71B25] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Working Hours</h3>
                    <p className="text-gray-600">Sunday - Thursday: 7:00 AM - 5:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#E71B25] rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Vita Trading Company</h3>
                    <p className="text-gray-600">Specialized in contracting, oil and gas equipment, road construction, and automotive services</p>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div className="mt-12">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Certifications & Standards</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-2 bg-red-50 rounded-lg flex items-center justify-center">
                      <i className="ri-shield-check-line text-[#E71B25] text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium">HSE</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-2 bg-red-50 rounded-lg flex items-center justify-center">
                      <i className="ri-award-line text-[#E71B25] text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium">ISO 9001</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-2 bg-red-50 rounded-lg flex items-center justify-center">
                      <i className="ri-medal-line text-[#E71B25] text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium">AMAL</p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-2 bg-red-50 rounded-lg flex items-center justify-center">
                      <i className="ri-verified-badge-line text-[#E71B25] text-2xl"></i>
                    </div>
                    <p className="text-sm font-medium">API</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Send Us a Message</h2>
              
              <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    value={contactFormData.fullName}
                    onChange={(e) => setContactFormData(prev => ({ ...prev, fullName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-sm"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={contactFormData.email}
                    onChange={(e) => setContactFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-sm"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject (Optional)
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={contactFormData.subject}
                    onChange={(e) => setContactFormData(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-sm"
                    placeholder="Enter the topic of your message"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    value={contactFormData.message}
                    onChange={(e) => setContactFormData(prev => ({ ...prev, message: e.target.value }))}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E71B25] focus:border-transparent text-sm resize-none"
                    placeholder="Enter your message (maximum 500 characters)"
                  />
                  <p className="text-xs text-gray-500 mt-1">{contactFormData.message.length}/500</p>
                </div>

                {contactFormStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    Message sent successfully! We will contact you soon.
                  </div>
                )}

                {contactFormStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    Error sending message. Please try again.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={contactFormStatus === 'submitting'}
                  className="w-full bg-[#E71B25] text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-medium whitespace-nowrap disabled:opacity-50"
                >
                  {contactFormStatus === 'submitting' ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending...
                    </span>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2B2C2E] text-white">
        <div className="container mx-auto px-4 py-16">
          <div className="hidden md:grid md:grid-cols-4 gap-12">
            <div className="space-y-6 md:col-span-2">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 flex items-center justify-center mr-4">
                    <img 
                      alt="VIPA Trading Company" 
                      className="h-20 w-auto" 
                      src="https://static.readdy.ai/image/b722d8dcf1c144135667d61789b02d4e/99b5c3dffef714fa12a155204a44cba1.png"
                    />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-1">VIPA Trading Company</h2>
                    <p className="text-[#E71B25] text-sm font-medium">شركة فيبا التجارية</p>
                  </div>
                </div>
                <p className="text-gray-300 text-base leading-relaxed mb-6">
                  A leading Kuwaiti multi-activity company, we provide integrated solutions in construction and roads, oil, gold, armament, and consulting with the highest global quality standards.
                </p>
                <div className="space-y-3">
                  <a href="mailto:info@vipatradingco.com" className="text-gray-300 hover:text-[#E71B25] transition-colors text-base inline-flex items-center cursor-pointer group">
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center mr-3 group-hover:bg-[#E71B25] transition-colors">
                      <i className="ri-mail-line text-sm"></i>
                    </div>
                    info@vipatradingco.com
                  </a>
                  <div className="text-gray-300 text-base inline-flex items-center">
                    <div className="w-8 h-8 bg-gray-700 rounded-lg flex items-center justify-center mr-3">
                      <i className="ri-map-pin-line text-sm"></i>
                    </div>
                    Kuwait
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-white font-bold text-xl mb-8 relative">
                Quick Links
                <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-[#E71B25]"></div>
              </h3>
              <ul className="space-y-4">
                <li><Link className="text-gray-300 hover:text-[#E71B25] transition-colors text-base py-2 cursor-pointer block group" to="/"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>Home</Link></li>
                <li><Link className="text-gray-300 hover:text-[#E71B25] transition-colors text-base py-2 cursor-pointer block group" to="/services"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>Services</Link></li>
                <li><Link className="text-gray-300 hover:text-[#E71B25] transition-colors text-base py-2 cursor-pointer block group" to="/about"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>About Us</Link></li>
                <li><Link className="text-[#E71B25] hover:text-[#d11620] transition-colors text-base py-2 cursor-pointer block group" to="/contact"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-bold text-xl mb-8 relative">
                Our Services
                <div className="absolute bottom-0 left-0 w-12 h-0.5 bg-[#E71B25]"></div>
              </h3>
              <ul className="space-y-4">
                <li><Link className="text-gray-300 hover:text-[#E71B25] transition-colors text-base py-2 cursor-pointer block group" to="/services/construction"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>Construction & Roads</Link></li>
                <li><Link className="text-gray-300 hover:text-[#E71B25] transition-colors text-base py-2 cursor-pointer block group" to="/services/oil"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>Oil & Gas</Link></li>
                <li><Link className="text-gray-300 hover:text-[#E71B25] transition-colors text-base py-2 cursor-pointer block group" to="/services/gold"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>Gold Trading</Link></li>
                <li><Link className="text-gray-300 hover:text-[#E71B25] transition-colors text-base py-2 cursor-pointer block group" to="/services/defense"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>Defense Systems</Link></li>
                <li><Link className="text-gray-300 hover:text-[#E71B25] transition-colors text-base py-2 cursor-pointer block group" to="/consultation"><i className="ri-arrow-right-s-line mr-2 group-hover:translate-x-1 transition-transform"></i>Consulting</Link></li>
              </ul>
            </div>
          </div>

          {/* Mobile Footer */}
          <div className="md:hidden space-y-6">
            <div className="space-y-4 pb-8 border-b border-gray-600">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 flex items-center justify-center mr-3">
                  <img 
                    alt="VIPA Trading Company" 
                    className="h-16 w-auto" 
                    src="https://static.readdy.ai/image/b722d8dcf1c144135667d61789b02d4e/99b5c3dffef714fa12a155204a44cba1.png"
                  />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">VIPA Trading Company</h2>
                  <p className="text-[#E71B25] text-xs">شركة فيبا التجارية</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">
                A leading Kuwaiti multi-activity company providing integrated solutions in various fields with the highest quality standards.
              </p>
              <div className="space-y-3">
                <a href="mailto:info@vipatradingco.com" className="text-gray-300 hover:text-[#E71B25] transition-colors text-sm inline-flex items-center cursor-pointer">
                  <i className="ri-mail-line mr-2"></i>info@vipatradingco.com
                </a>
                <div className="text-gray-300 text-sm inline-flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>Kuwait
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-gray-600">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <p className="text-gray-400 text-sm">© 2025 VIPA Trading Company — All rights reserved.</p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-4">
                  <a href="#" className="w-10 h-10 bg-gray-700 hover:bg-[#E71B25] rounded-lg flex items-center justify-center transition-colors text-lg cursor-pointer group">
                    <i className="ri-linkedin-fill group-hover:scale-110 transition-transform"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-700 hover:bg-[#E71B25] rounded-lg flex items-center justify-center transition-colors text-lg cursor-pointer group">
                    <i className="ri-instagram-fill group-hover:scale-110 transition-transform"></i>
                  </a>
                  <a href="#" className="w-10 h-10 bg-gray-700 hover:bg-[#E71B25] rounded-lg flex items-center justify-center transition-colors text-lg cursor-pointer group">
                    <i className="ri-twitter-fill group-hover:scale-110 transition-transform"></i>
                  </a>
                </div>
                <div className="flex items-center gap-3 md:border-l md:border-gray-600 md:pl-4">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <i className="ri-medal-line text-[#E71B25]"></i>
                    <span>ISO 9001</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <i className="ri-shield-check-line text-[#A71B25]"></i>
                    <span>HSE Certified</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
